/**
 * База знаний: пространства, дерево страниц и сама страница.
 *
 * Сервис knowledge отдаёт дерево одним запросом плоским списком с родителями —
 * собирать его в узлы дешевле на клиенте, чем ходить за каждым уровнем.
 */
import { pageApi, spaceApi } from '@/api'

import type {
  V1Page,
  V1PageBrief,
  V1PageIssueLink,
} from '@/api/clients/knowledge-page'
import type { V1Space } from '@/api/clients/knowledge-space'

export interface Space {
  id: string
  key: string
  name: string
  description: string
  pageCount: number
}

export interface PageBrief {
  id: string
  parentId: string
  title: string
  position: number
  hasChildren: boolean
}

/** Узел дерева: страница и её дети */
export interface PageNode extends PageBrief {
  children: PageNode[]
}

export interface Page {
  id: string
  spaceId: string
  parentId: string
  title: string
  contentMd: string
  createdBy: string
  updatedBy: string
  updatedAt: string
  version: number
}

export interface PageIssueLink {
  id: string
  issueId: string
  issueKey: string
  issueTitle: string
}

const spaceOf = (space: V1Space): Space => ({
  id: space.id ?? '',
  key: space.key ?? '',
  name: space.name ?? '',
  description: space.description ?? '',
  pageCount: Number(space.pageCount ?? 0),
})

const briefOf = (page: V1PageBrief): PageBrief => ({
  id: page.id ?? '',
  parentId: page.parentId ?? '',
  title: page.title ?? '',
  position: Number(page.position ?? 0),
  hasChildren: Boolean(page.hasChildren),
})

const pageOf = (page: V1Page): Page => ({
  id: page.id ?? '',
  spaceId: page.spaceId ?? '',
  parentId: page.parentId ?? '',
  title: page.title ?? '',
  contentMd: page.contentMd ?? '',
  createdBy: page.createdBy ?? '',
  updatedBy: page.updatedBy ?? '',
  updatedAt: page.updatedAt ?? '',
  version: Number(page.version ?? 0),
})

const linkOf = (link: V1PageIssueLink): PageIssueLink => ({
  id: link.id ?? '',
  issueId: link.issueId ?? '',
  issueKey: link.issueKey ?? '',
  issueTitle: link.issueTitle ?? '',
})

export const listSpaces = async (): Promise<Space[]> => {
  const { data } = await spaceApi.spaceServiceListSpaces()

  return (data.spaces ?? []).map(spaceOf)
}

export const getTree = async (spaceId: string): Promise<PageBrief[]> => {
  const { data } = await pageApi.pageServiceGetPageTree(spaceId)

  return (data.pages ?? []).map(briefOf)
}

export const getPage = async (pageId: string): Promise<Page> => {
  const { data } = await pageApi.pageServiceGetPage(pageId)

  return pageOf(data.page ?? {})
}

export const listIssueLinks = async (pageId: string): Promise<PageIssueLink[]> => {
  const { data } = await pageApi.pageServiceListIssueLinks(pageId)

  return (data.links ?? []).map(linkOf)
}

/**
 * Дерево из плоского списка. Страница, чей родитель в выдачу не попал (его
 * удалили или он в другом пространстве), поднимается в корень: потерять её
 * молча хуже, чем показать не на своём уровне.
 */
export const toTree = (pages: PageBrief[]): PageNode[] => {
  const nodes = new Map<string, PageNode>(
    pages.map((page) => [page.id, { ...page, children: [] }]),
  )
  const roots: PageNode[] = []

  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined

    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  const sort = (list: PageNode[]) => {
    list.sort((a, b) => a.position - b.position || a.title.localeCompare(b.title))
    list.forEach((node) => sort(node.children))
  }

  sort(roots)

  return roots
}

/** Путь до страницы — им подписывают крошки над заголовком. */
export const pathTo = (pages: PageBrief[], pageId: string): PageBrief[] => {
  const byId = new Map(pages.map((page) => [page.id, page]))
  const path: PageBrief[] = []

  let current = byId.get(pageId)

  while (current) {
    path.unshift(current)
    current = current.parentId ? byId.get(current.parentId) : undefined
    // Цикл в родителях положил бы вкладку: страница не может быть своим предком.
    if (path.length > 20) break
  }

  return path
}
